import React from "react";

/*
  AssetConditionMatrixGrid
  - Pure component (no global layout)
  - Receives matrixData + colours
  - Renders: column headers (grey tiles), row headers (grey tiles), coloured cells with number + SVG pie
*/

export default function AssetConditionMatrixGrid(props) {

    const matrixData = props.matrixData || {};
    const rows = props.rows || [];
    const cols = props.cols || [];
    const ragColor = props.ragColor || function () { return "#eee"; };

    /* =========================
       STYLES (LOCAL ONLY)
    ========================== */

    const headerTile = {
        //background: "rgba(240,242,245,0.9)",
        borderRadius: "10px",
        height: "54px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        //boxShadow: "0 10px 22px rgba(0,0,0,0.08)",
        fontSize: "12px",
        color: "#333",
        fontWeight: 600
    };

    const headerTitle = {
        fontSize: "12px",
        fontWeight: 600,
        color: "#333",
        lineHeight: "14px"
    };

    const headerValue = {
        fontSize: "14px",
        fontWeight: 700,
        color: "#111",
        marginTop: "2px"
    };

    const rowTile = {
        //background: "rgba(240,242,245,0.9)",
        borderRadius: "12px",
        padding: "18px 16px",
        minHeight: "110px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        //boxShadow: "0 10px 22px rgba(0,0,0,0.08)",
        color: "#333",
        fontWeight: 600
    };

    const rowTitle = {
        fontSize: "12px",
        fontWeight: 600,
        color: "#333",
        lineHeight: "14px"
    };

    const rowValue = {
        fontSize: "16px",
        fontWeight: 800,
        color: "#111",
        marginTop: "6px"
    };

    const cellBase = {
        minHeight: "110px",
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        position: "relative",
		paddingBottom:'10px'
    };

    const cellNumber = {
        position: "absolute",
        top: "14px",
        left: "0px",
        right: "0px",
        textAlign: "center",
        fontSize: "18px",
        fontWeight: 800,
        color: "white"
    };
	
	
	
	const breakdownBar = {
	    display: "inline-flex",
	    gap: "6px",
	    padding: "6px",
	    borderRadius: "20px",
	    background: "rgba(0,0,0,0.04)",
	    boxShadow: "inset 0 1px 2px rgba(0,0,0,0.06)",
		
	};
	
	
	const breakdownOption = (active) => ({
	    padding: "8px 14px",
	    fontSize: "12px",
	    borderRadius: "16px",
	    cursor: "pointer",
	    background: active ? "#111" : "transparent",
	    color: active ? "#fff" : "#444",
	    transition: "all 0.18s ease",
	    whiteSpace: "nowrap"
	});
	
	
	const handleMatrixClick = () => {
		if(props.openDetailDrawer){
			props.openDetailDrawer();
		}
	}

    /* =========================
       SVG PIE (LOCAL)
    ========================== */

	function MiniPie(props) {

	    const parts =  [
	        { label: "HVAC", value: 12 },
	        { label: "Lighting", value: 8 },
	        { label: "IT", value: 5 }
	    ];

	    const size = props.size || 62;
	    const radius = Math.floor(size / 2) - 2;
	    const cx = Math.floor(size / 2);
	    const cy = Math.floor(size / 2);

		const colours = [
		    "rgba(93,173,226,0.62)",  // HVAC (blue)
		    "rgba(72,201,176,0.42)",  // Lighting (teal)
		    "rgba(175,122,197,0.82)", // IT (purple)
		    "rgba(245,178,65,0.22)",  // Fire (amber)
		    "rgba(236,112,99,0.22)",  // Security (red)
		    "rgba(82,190,128,0.22)"   // Controls (green)
		];

	    function polarToCartesian(x, y, r, angle) {
	        const a = (angle - 90) * Math.PI / 180.0;
	        return {
	            x: x + r * Math.cos(a),
	            y: y + r * Math.sin(a)
	        };
	    }

	    function describeArc(x, y, r, start, end) {
	        const s = polarToCartesian(x, y, r, end);
	        const e = polarToCartesian(x, y, r, start);
	        const large = end - start <= 180 ? "0" : "1";

	        return [
	            "M", x, y,
	            "L", s.x, s.y,
	            "A", r, r, 0, large, 0, e.x, e.y,
	            "Z"
	        ].join(" ");
	    }

	    const total = parts.reduce((s, p) => s + p.value, 0) || 1;
	    let start = 0;

	    return (
	        <svg
	            width={size}
	            height={size}
	            style={{
	                opacity: 0.28,
	                marginTop: "36px",
	                pointerEvents: "all",
					curso:'pointer'
	            }}
	        >
	            {parts.map((p, i) => {

	                const angle = (p.value / total) * 360;
	                const end = start + angle;
	                const d = describeArc(cx, cy, radius, start, end);
	                start = end;

	                return (
						<path
						    key={i}
						    d={d}
						    fill="rgba(255,255,255,0.50)"
						    stroke="rgba(100,100,100,0.2)"
						    strokeWidth="2"
						    pointerEvents="all"
						>
						    <title>{p.label} : {p.value}</title>
						</path>
	                );
	            })}

	            <circle
	                cx={cx}
	                cy={cy}
	                r={radius}
	                fill="none"
	                stroke="rgba(0,0,0,0.12)"
	                pointerEvents="none"
	            />
	        </svg>
	    );
	}

    /* =========================
       TOTALS
    ========================== */

    function getColumnTotal(colName) {
        let t = 0;
        rows.forEach(function (r) {
            const cell = matrixData[r] && matrixData[r][colName];
            if (cell && cell.total) {
                t += cell.total;
            }
        });
        return t;
    }

    function getRowTotal(rowName) {
        let t = 0;
        cols.forEach(function (c) {
            const cell = matrixData[rowName] && matrixData[rowName][c];
            if (cell && cell.total) {
                t += cell.total;
            }
        });
        return t;
    }

    /* =========================
       RENDER GRID
    ========================== */

    return (
        <div>

            {/* COLUMN HEADER TILES */}
            <div style={{
                display: "grid",
                gridTemplateColumns: "160px repeat(" + cols.length + ", 1fr)",
                gap: "10px",
                marginBottom: "10px"
            }}>
                <div />
                {cols.map(function (c) {
                    return (
                        <div key={c} style={headerTile}>
                            <div style={headerTitle}>{c}</div>
                            <div style={headerValue}>{getColumnTotal(c)}</div>
                        </div>
                    );
                })}
            </div>

            {/* MATRIX BODY */}
            <div style={{
                borderRadius: "12px",
                overflow: "hidden"
            }}>
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "160px repeat(" + cols.length + ", 1fr)",
                    gap: "0px"
                }}>

                    {rows.map(function (r, rowIndex) {

                        return (
                            <React.Fragment key={r}>

                                {/* ROW HEADER TILE */}
                                <div style={rowTile}>
                                    <div style={rowTitle}>{r}</div>
                                    <div style={rowValue}>{getRowTotal(r)}</div>
                                </div>

                                {/* CELLS */}
								{cols.map(function (c, colIndex) {

								    const cell =
								        (matrixData[r] && matrixData[r][c])
								            ? matrixData[r][c]
								            : { total: 0, breakdown: [1, 1, 1] };

								    const bg = ragColor(r, c);

								    const isTopLeft = rowIndex === 0 && colIndex === 0;
								    const isBottomLeft = rowIndex === rows.length - 1 && colIndex === 0;

								    const cellStyle = {
								        ...cellBase,
								        background: bg,
								        borderTopLeftRadius: isTopLeft ? "14px" : undefined,
								        borderBottomLeftRadius: isBottomLeft ? "14px" : undefined
								    };

								    return (
								        <div
										    onClick={handleMatrixClick}
								            key={r + "_" + c}
								            style={cellStyle}
								            title={r + " / " + c + " : " + cell.total}
								        >
								            <div style={cellNumber}>{cell.total}</div>
								            <MiniPie parts={cell.breakdown} />
								        </div>
								    );
								})}

                            </React.Fragment>
                        );
                    })}

                </div>
            </div>
			<div style={{ marginTop: "42px", textAlign: "center" }}>
			    <div style={{ fontSize: "11px", color: "#777", marginBottom: "6px" }}>
			        Breakdown by
			    </div>

			    <div style={breakdownBar}>
			        <div style={breakdownOption(true)}>Asset Class</div>
			        <div style={breakdownOption(false)}>Manufacturer</div>
			        <div style={breakdownOption(false)}>Condition</div>
			        <div style={breakdownOption(false)}>Age</div>
			        <div style={breakdownOption(false)}>Warranty</div>
			    </div>
			</div>
        </div>
    );
}